import { escapeHTML } from '../../utils/escape.js';
import { formatFechaCorta } from '../../utils/formatters.js';
import { agruparPorCategoria, buscar } from '../../shared/faq-search-policy.js';

/**
 * Staff "Preguntas" section (openspec/changes/staff-faq/spec.md B1, B2, B4,
 * B10 and its UI brief). Pure render functions, no state: `js/faq.js` owns
 * state and listeners.
 *
 * Two pieces on purpose: the shell (title + search box) renders once per
 * mount, and only the results region re-renders on every keystroke — a full
 * re-render would replace the input and drop the caret mid-word.
 */

const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted';

/** @returns {string} */
export function FaqStaffShell() {
    return `
        <section data-faq-staff class="max-w-3xl">
            <header class="mb-6">
                <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Preguntas frecuentes</h2>
                <p class="mt-1 text-sm text-text-muted">Escribí tu duda como se la preguntarías a alguien.</p>
            </header>
            <div class="relative mb-8">
                <i class="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden="true"></i>
                <input type="search" data-faq-buscar autocomplete="off" spellcheck="false"
                    aria-label="Buscar en las preguntas frecuentes" aria-controls="faq-staff-resultados"
                    placeholder="Ej.: no pasa la tarjeta"
                    class="w-full bg-surface border border-border-subtle rounded-2xl pl-12 pr-4 py-4 text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
            <div id="faq-staff-resultados" aria-live="polite"></div>
        </section>
    `;
}

/**
 * @param {Record<string, any>} e
 * @param {boolean} abierta
 */
function renderEntrada(e, abierta) {
    const id = escapeHTML(e.id);
    const ojo = e.ojo
        ? `<p data-faq-ojo class="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-sm font-bold text-amber-700 dark:text-amber-300">
                <i class="fa-solid fa-triangle-exclamation mt-0.5 shrink-0" aria-hidden="true"></i>
                <span>${escapeHTML(e.ojo)}</span>
           </p>`
        : '';
    const pasos = (e.pasos ?? []).length
        ? `<div class="mt-4">
                <span class="${LABEL} mb-2">Pasos</span>
                <ol data-faq-pasos class="list-decimal pl-5 space-y-1.5 text-sm text-text-primary marker:font-black marker:text-text-muted">
                    ${e.pasos.map((p) => `<li>${escapeHTML(p)}</li>`).join('')}
                </ol>
           </div>`
        : '';
    const avisaA = e.avisa_a
        ? `<p class="mt-4 text-sm text-text-primary"><span class="${LABEL} mb-1">Avisá a</span><span data-faq-avisa-a>${escapeHTML(e.avisa_a)}</span></p>`
        : '';
    const detalle = abierta
        ? `<div id="faq-detalle-${id}" class="mt-2 border-t border-border-subtle pt-2">
                ${pasos}
                ${avisaA}
                <p data-faq-actualizado class="mt-4 text-[11px] text-text-muted">Actualizado el ${escapeHTML(formatFechaCorta(e.updated_at))}</p>
           </div>`
        : '';

    return `
        <article data-faq-entrada="${id}" class="bg-surface border border-border-subtle rounded-2xl px-5 py-4">
            <button type="button" data-action="faq-abrir" data-id="${id}" aria-expanded="${abierta}" aria-controls="faq-detalle-${id}"
                class="w-full flex items-start justify-between gap-4 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-4 focus-visible:ring-offset-surface">
                <span class="min-w-0">
                    <span data-faq-pregunta class="block font-black text-text-primary">${escapeHTML(e.pregunta)}</span>
                    <span data-faq-en-corto class="block mt-1.5 text-[15px] leading-relaxed text-text-primary">${escapeHTML(e.en_corto)}</span>
                </span>
                <i class="fa-solid fa-chevron-down mt-1 shrink-0 text-text-muted transition-transform duration-200 ${abierta ? 'rotate-180' : ''}" aria-hidden="true"></i>
            </button>
            ${ojo}
            ${detalle}
        </article>
    `;
}

/** @param {{ reportada: boolean, reportando: boolean, prominente: boolean }} p */
function renderReporte({ reportada, reportando, prominente }) {
    if (reportando) {
        return `
            <button type="button" data-action="faq-no-encontre" disabled
                class="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">
                <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> Enviando...
            </button>`;
    }
    if (reportada) {
        return `
            <button type="button" data-action="faq-no-encontre" disabled
                class="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">
                <i class="fa-solid fa-check" aria-hidden="true"></i> Gracias, se lo pasamos al admin
            </button>`;
    }
    const estilo = prominente
        ? 'bg-brand text-white shadow-lg active:scale-95 transition-transform'
        : 'border border-border-subtle text-text-muted hover:text-brand transition-colors';
    return `
        <button type="button" data-action="faq-no-encontre"
            class="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest ${estilo}">
            <i class="fa-solid fa-flag" aria-hidden="true"></i> No encontré lo que buscaba
        </button>`;
}

/**
 * @param {{
 *   uiState: { status: 'loading' | 'error' | 'ready', mensaje?: string },
 *   entradas: Array<Record<string, any>>,
 *   consulta: string,
 *   abiertas: Set<string>,
 *   reportada: boolean,
 *   reportando?: boolean,
 * }} props
 * @returns {string}
 */
export function FaqStaffResultados({ uiState, entradas, consulta, abiertas, reportada, reportando = false }) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando preguntas...</p>
            </div>`;
    }
    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar las preguntas.')}</p>
                <button type="button" data-action="faq-reintentar"
                    class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>`;
    }
    if (entradas.length === 0) {
        return '<p class="py-12 text-center text-sm text-text-muted">Todavía no hay preguntas cargadas.</p>';
    }

    if (consulta.trim()) {
        const resultados = buscar(entradas, consulta);
        if (resultados.length === 0) {
            return `
                <div class="py-10 text-center">
                    <p class="font-black text-text-primary">No encontramos nada con esas palabras.</p>
                    <p class="mt-1 text-sm text-text-muted">Probá con otras, o avisale al admin que falta esta pregunta.</p>
                    ${renderReporte({ reportada, reportando, prominente: true })}
                </div>`;
        }
        return `
            <p class="${LABEL} mb-3">${resultados.length} ${resultados.length === 1 ? 'respuesta' : 'respuestas'}</p>
            <div class="flex flex-col gap-3">${resultados.map((e) => renderEntrada(e, abiertas.has(e.id))).join('')}</div>
            ${renderReporte({ reportada, reportando, prominente: false })}`;
    }

    const grupos = agruparPorCategoria(entradas);
    const chips = grupos
        .map(
            ({ categoria }, i) => `
                <button type="button" data-action="faq-ir-categoria" data-destino="faq-cat-${i}"
                    class="px-4 py-2 rounded-full bg-surface border border-border-subtle text-xs font-bold text-text-primary hover:border-brand hover:text-brand transition-colors">
                    ${escapeHTML(categoria)}
                </button>`,
        )
        .join('');
    const secciones = grupos
        .map(
            ({ categoria, entradas: lista }, i) => `
                <section data-faq-categoria="${escapeHTML(categoria)}" id="faq-cat-${i}" class="scroll-mt-6">
                    <h3 class="${LABEL} mb-3">${escapeHTML(categoria)}</h3>
                    <div class="flex flex-col gap-3">${lista.map((e) => renderEntrada(e, abiertas.has(e.id))).join('')}</div>
                </section>`,
        )
        .join('');
    return `
        <nav aria-label="Categorías" class="flex flex-wrap gap-2 mb-8">${chips}</nav>
        <div class="flex flex-col gap-10">${secciones}</div>`;
}
